import React, { useState, useEffect, useReducer } from 'react';
import { Landmark, Wallet, CreditCard, Building2, Hash, User, Save, CheckCircle } from 'lucide-react';
// import lib
import handleReducer, { textChange, onBlur } from '../lib/handleChange';
import isEmpty from '../lib/isEmpty';
import toastAlert from '../lib/toastAlert';
// import validation
import { bankDetailsValid, upiDetailsValid } from '../validation/user.validation';
// import services
import { getBankDetails, updateBankDetails } from '../services/user.service';

const initialBankvalue = {
    accountHolderName: '',
    bankName: '',
    branchName: '',
    ifscCode: '',
    accountNumber: '',
    isSaved: false
};

const initialUpivalue = {
    // UPI Details
    upiProvider: '',
    upiUsername: '',
    upiNumber: '',
    isSaved: false
};

const BankAccount = () => {

    // state
    const [paymentType, setPaymentType] = useState('bank');
    const [bankDetails, setBankDetails] = useReducer(handleReducer, initialBankvalue);
    const [upiDetails, setUpiDetails] = useReducer(handleReducer, initialUpivalue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loader, setLoader] = useState(false);

    const { accountHolderName, bankName, branchName, ifscCode, accountNumber } = bankDetails;
    const { upiProvider, upiUsername, upiNumber } = upiDetails;

    // function
    const handleChange = e => textChange(e, paymentType == 'bank' ? setBankDetails : setUpiDetails);
    const handleBlur = e => onBlur(e, setTouched);

    const handlePaymentTypeChange = (type) => {
        // if (type == 'bank') setUpiDetails({ type: 'RESET', value: initialUpivalue });
        // else setBankDetails({ type: 'RESET', value: initialBankvalue });
        setPaymentType(type);
        setErrors({});
        setTouched({});
    };

    const validForm = () => {
        try {
            if (paymentType == 'bank') {
                let parsed = JSON.parse(JSON.stringify(bankDetails))
                delete parsed.isSaved;
                let payload = { ...parsed, ...{ paymentType } }
                return bankDetailsValid(payload);
            } else {
                let parsed = JSON.parse(JSON.stringify(upiDetails))
                delete parsed.isSaved;
                let payload = { ...parsed, ...{ paymentType } }
                return upiDetailsValid(payload);
            }
        } catch (error) { }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoader(true);
            let payload = {};
            if (paymentType == 'bank') {
                let parsed = JSON.parse(JSON.stringify(bankDetails))
                delete parsed.isSaved;
                payload = { ...parsed, ...{ paymentType } }
            } else {
                let parsed = JSON.parse(JSON.stringify(upiDetails))
                delete parsed.isSaved;
                payload = { ...parsed, ...{ paymentType } }
            }

            const { status, message, errors } = await updateBankDetails(payload);
            setLoader(false)
            if (status === true) {
                toastAlert('success', message, 'bank-account');
                initialCall()
            } else if (!isEmpty(errors)) {
                setErrors(errors);
            } else if (message) {
                toastAlert('error', message, 'bank-account');
            }
        } catch (error) {
            console.error('Error saving bank details:', error);
            toastAlert('error', 'Failed to save bank details', 'bank-account');
            setLoader(false);
        }
    };

    const initialCall = async () => {
        try {
            const { status, result } = await getBankDetails();
            if (status === true) {
                setBankDetails({ type: 'INITIAL_SET', value: result?.bankDetails });
                setUpiDetails({ type: 'INITIAL_SET', value: result?.upiDetails });
            }
        } catch (error) { }
    }

    useEffect(() => {
        const { status, errors } = validForm();
        if (!status) setErrors(errors);
        else setErrors({});
    }, [paymentType, bankDetails, upiDetails]);

    useEffect(() => {
        initialCall()
    }, [])

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full">
                            <Landmark className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Bank Account Details</h1>
                            <p className="text-gray-600 text-sm">Add or update your payment information</p>
                        </div>
                    </div>
                </div>

                {/* Main Form */}
                <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
                    <form className="space-y-6">
                        {/* Payment Type Selection */}
                        <div>
                            <label className="block text-lg font-semibold text-gray-800 mb-4">
                                Select Payment Method
                            </label>
                            <div className="grid md:grid-cols-2 gap-4">
                                {/* Bank Details Radio */}
                                <div
                                    onClick={() => handlePaymentTypeChange('bank')}
                                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${paymentType === 'bank'
                                        ? 'border-cyan-500 bg-cyan-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${paymentType === 'bank'
                                                ? 'border-cyan-500 bg-cyan-500'
                                                : 'border-gray-300'
                                                }`}>
                                                {paymentType === 'bank' && (
                                                    <div className="w-3 h-3 bg-white rounded-full"></div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Building2 className="w-5 h-5 text-cyan-600" />
                                                <h3 className="font-semibold text-gray-800">Bank Details</h3>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                Add your bank account information for direct transfers
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* UPI Payment Radio */}
                                <div
                                    onClick={() => handlePaymentTypeChange('upi')}
                                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${paymentType === 'upi'
                                        ? 'border-cyan-500 bg-cyan-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${paymentType === 'upi'
                                                ? 'border-cyan-500 bg-cyan-500'
                                                : 'border-gray-300'
                                                }`}>
                                                {paymentType === 'upi' && (
                                                    <div className="w-3 h-3 bg-white rounded-full"></div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Wallet className="w-5 h-5 text-cyan-600" />
                                                <h3 className="font-semibold text-gray-800">UPI Payment</h3>
                                            </div>
                                            <p className="text-sm text-gray-600">
                                                Add your UPI details for instant payments
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bank Details Form */}
                        {paymentType === 'bank' && (
                            <div className="space-y-5 border-t pt-6">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-cyan-600" />
                                    Bank Account Information
                                </h3>

                                {/* Account Holder Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Account Holder Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="accountHolderName"
                                            value={accountHolderName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter account holder name"
                                            disabled={bankDetails?.isSaved}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.accountHolderName && touched.accountHolderName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.accountHolderName && touched.accountHolderName && (
                                        <p className="text-red-500 text-sm mt-1">{errors.accountHolderName}</p>
                                    )}
                                </div>

                                {/* Bank Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Bank Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="bankName"
                                            value={bankName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter bank name"
                                            disabled={bankDetails?.isSaved}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.bankName && touched.bankName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.bankName && touched.bankName && (
                                        <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
                                    )}
                                </div>

                                {/* Branch Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Branch Name <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="branchName"
                                            value={branchName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter branch name"
                                            disabled={bankDetails?.isSaved}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.branchName && touched.branchName ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.branchName && touched.branchName && (
                                        <p className="text-red-500 text-sm mt-1">{errors.branchName}</p>
                                    )}
                                </div>

                                {/* IFSC Code */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        IFSC Code <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="ifscCode"
                                            value={ifscCode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="e.g., ICIC0006216"
                                            disabled={bankDetails?.isSaved}
                                            maxLength={11}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg uppercase focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.ifscCode && touched.ifscCode ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.ifscCode && touched.ifscCode && (
                                        <p className="text-red-500 text-sm mt-1">{errors.ifscCode}</p>
                                    )}
                                </div>

                                {/* Account Number */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Account Number <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="accountNumber"
                                            value={accountNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter account number"
                                            disabled={bankDetails?.isSaved}
                                            maxLength={18}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.accountNumber && touched.accountNumber ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.accountNumber && touched.accountNumber && (
                                        <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* UPI Payment Form */}
                        {paymentType === 'upi' && (
                            <div className="space-y-5 border-t pt-6">
                                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                                    <Wallet className="w-5 h-5 text-cyan-600" />
                                    UPI Payment Information
                                </h3>

                                {/* UPI Provider */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        UPI Provider <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Wallet className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <select
                                            name="upiProvider"
                                            value={upiProvider}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={upiDetails?.isSaved}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.upiProvider && touched.upiProvider ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        >
                                            <option value="">Select UPI provider</option>
                                            <option value="GPay">Google Pay (GPay)</option>
                                            <option value="PhonePe">PhonePe</option>
                                        </select>
                                    </div>
                                    {errors.upiProvider && touched.upiProvider && (
                                        <p className="text-red-500 text-sm mt-1">{errors.upiProvider}</p>
                                    )}
                                </div>

                                {/* UPI Username */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        {upiProvider} Username <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="upiUsername"
                                            value={upiUsername}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter username"
                                            disabled={upiDetails?.isSaved}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.upiUsername && touched.upiUsername ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.upiUsername && touched.upiUsername && (
                                        <p className="text-red-500 text-sm mt-1">{errors.upiUsername}</p>
                                    )}
                                </div>

                                {/* UPI Number */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        UPI Number (Mobile) <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type="text"
                                            name="upiNumber"
                                            value={upiNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder="Enter 10-digit mobile number"
                                            maxLength={10}
                                            disabled={upiDetails?.isSaved}
                                            className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.upiNumber && touched.upiNumber ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                        />
                                    </div>
                                    {errors.upiNumber && touched.upiNumber && (
                                        <p className="text-red-500 text-sm mt-1">{errors.upiNumber}</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Save Button */}
                        {
                            ((paymentType == 'bank' && bankDetails?.isSaved == false) || (paymentType == 'upi' && upiDetails?.isSaved == false)) && (
                                <div className="border-t pt-6">
                                    <button
                                        type="submit"
                                        disabled={loader || !isEmpty(errors)}
                                        className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        onClick={handleSubmit}
                                    >
                                        {loader ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Save className="w-5 h-5" />
                                                Save Details
                                            </>
                                        )}
                                    </button>
                                </div>
                            )
                        }
                    </form>
                </div>

                {/* Info Card */}
                <div className="bg-blue-50 rounded-xl shadow-lg p-6 mt-6 border border-blue-200">
                    <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-gray-800 mb-2">Important Information</h3>
                            <ul className="space-y-2 text-sm text-gray-700">
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                                    <span>Ensure all details are accurate to avoid payment delays</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                                    <span>Bank account should be in your name for verification</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                                    <span>UPI payments are processed instantly</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                                    <span style={{ color: 'red' }}>Once you update the details, you will no longer be able to edit them. Please contact the admin if you need any further changes.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BankAccount;